import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import PropTypes from 'prop-types'
import { useState } from 'react'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'
import Selector, { TabProps } from './Selector'
import Switcher from '../Switcher'
import ItemCard from './items/ItemCard'
import classes from './DualPanelSelector.module.scss'
import { DUAL_PANEL_SELECTABLE_TYPES } from '../../../constants/dual-panel-selector-constants'

const SWITCHER_OPTIONS = {
  [DUAL_PANEL_SELECTABLE_TYPES.students]: 'common.dualPanelSelector.selectStudents',
  [DUAL_PANEL_SELECTABLE_TYPES.programs]: 'common.dualPanelSelector.selectPrograms',
  [DUAL_PANEL_SELECTABLE_TYPES.programsWithPackages]: 'common.dualPanelSelector.selectPrograms',
  [DUAL_PANEL_SELECTABLE_TYPES.series]: 'common.dualPanelSelector.selectEvents',
}

const TABS = {
  [DUAL_PANEL_SELECTABLE_TYPES.students]: 'common.dualPanelSelector.allStudents',
  [DUAL_PANEL_SELECTABLE_TYPES.programs]: 'common.dualPanelSelector.programs',
  [DUAL_PANEL_SELECTABLE_TYPES.programsWithPackages]: 'common.dualPanelSelector.programs',
  [DUAL_PANEL_SELECTABLE_TYPES.series]: 'common.dualPanelSelector.nineHoleEvents',
  [DUAL_PANEL_SELECTABLE_TYPES.coaches]: 'common.dualPanelSelector.coaches',
}

const DualPanelSelector = ({
  tabTypes,
  tabProps,
  type,
  switcherPageTitle,
  switcherPageInfoText,
  selectedItems,
  selectedItemType,
  summaryTitle,
  summaryPlaceholder,
  onRemove,
  className,
}) => {
  const { t } = useTranslation()
  const [isSwitched, setIsSwitched] = useState(false)
  const summaryTitleText = summaryTitle ?? t('common.dualPanelSelector.summary', { count: selectedItems.length })
  const selectedType = isSwitched ? tabTypes[1] : tabTypes[0]
  const withSwitcherInsteadOfTabs = type === 'EventRoster'

  return (
    <div className={classnames('d-flex', className)}>
      {type === 'EventRoster' && (
        <div className={classnames('w-50 d-flex flex-column', classes.panel)}>
          {!!switcherPageTitle && <h3 className={classnames('mb-3', classes.title)}>{switcherPageTitle}</h3>}
          <Switcher
            isSwitched={isSwitched}
            onClick={() => setIsSwitched(prevState => !prevState)}
            options={tabTypes.map(tabType => t(SWITCHER_OPTIONS[tabType]))}
            className='mb-4 flex-shrink-0'
          />
          {!!switcherPageInfoText && <p>{switcherPageInfoText}</p>}
          <Selector
            className='flex-grow-1 '
            type={selectedType}
            infoText={switcherPageInfoText}
            {...tabProps[selectedType]}
          />
        </div>
      )}
      {type === 'ProgramRoster' && (
        <Tabs
          className={classnames('w-50 h-100 pt-0')}
          selectedTabClassName={classes.activeTab}
          selectedTabPanelClassName={classnames(classes.panel, classes.activeTabPanel, classes.bordered)}
        >
          <TabList className={classes.tabList}>
            {tabTypes.map((type, index) => (
              <Tab key={index} className={classes.tab}>{t(TABS[type])}</Tab>
            ))}
          </TabList>

          {tabTypes.map((type, index) => (
            <TabPanel key={index}>
              <Selector className='h-100' type={type} {...tabProps[type]} />
            </TabPanel>
          ))}
        </Tabs>
      )}
      {type === 'WaitlistProgramRoster' && (
        <div className={classnames(classes.panel, classes.activeTabPanel, classes.waitlistPanel, classes.bordered, 'w-50')}>
          <Selector className='h-100' type={DUAL_PANEL_SELECTABLE_TYPES.programs} {...tabProps[DUAL_PANEL_SELECTABLE_TYPES.programs]} />
        </div>
      )}
      {type === 'AddGolferRoster' && (
        <div className={classnames('w-50 d-flex flex-column', classes.panel, classes.bordered)}>
          {!!switcherPageTitle && <h3 className={classnames('mb-3', classes.title)}>{switcherPageTitle}</h3>}
          {!!switcherPageInfoText && <p>{switcherPageInfoText}</p>}
          <Selector
            className='flex-grow-1 '
            type={tabTypes[1]}
            infoText={switcherPageInfoText}
            {...tabProps[tabTypes[1]]}
          />
        </div>
      )}
      <div className={classnames(
        'w-50 d-flex flex-column',
        classes.panel,
        classes.right,
        {
          [classes.withTabs]: type === 'ProgramRoster',
          [classes.bordered]: !withSwitcherInsteadOfTabs,
        }
      )}>
        <h3 className={classes.title}>{summaryTitleText}</h3>

        {selectedItems.length > 0 ? selectedItems.map((i) => <ItemCard
          className='mb-3'
          key={i.id}
          type={i.type || selectedItemType}
          item={i}
          onRemove={onRemove}
        />
        ) : summaryPlaceholder}
      </div>
    </div>
  )
}

DualPanelSelector.propTypes = {
  // Left panel
  tabTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(DUAL_PANEL_SELECTABLE_TYPES))).isRequired,
  tabProps: PropTypes.objectOf(PropTypes.shape({
    ...TabProps,
  })).isRequired,
  type: PropTypes.string,
  switcherPageTitle: PropTypes.string,
  switcherPageInfoText: PropTypes.string,
  // Right panel
  selectedItems: PropTypes.array.isRequired,
  selectedItemType: PropTypes.oneOf(Object.values(DUAL_PANEL_SELECTABLE_TYPES)).isRequired,
  summaryTitle: PropTypes.string,
  summaryPlaceholder: PropTypes.node.isRequired,
  onRemove: PropTypes.func,
  className: PropTypes.string,
}

export default DualPanelSelector
